import {
  APIEntityType,
  Features,
  isComponent as isComponentEntity,
  ViewIds,
} from '@ardoq/api-types';
import { componentInterface } from '@ardoq/component-interface';
import {
  DropdownButtonOption,
  DropdownItem,
  DropdownOption,
  DropdownOptionType,
} from '@ardoq/dropdown-menu';
import { IconName } from '@ardoq/icons';
import {
  getEditPropertiesMenuItem,
  getDeleteReferenceMenuItem,
  getHistoryMenuItem,
} from 'contextMenus/menuItems';
import { rectCenter, rectEncloseRects } from '../misc/geometry';
import { BlocksViewLink, BlocksViewNode, Controller } from '../types';
import { getComponentMenu } from 'contextMenus/componentMenu';
import { getScenarioContextMenuOptionsForView } from 'scope/scenarioMenuOptions';
import { hasFeature } from 'modelInterface/features/featureInterface';
import { isTruthy, openUrlInNewTab } from '@ardoq/common-helpers';
import { UrlFieldValue } from '@ardoq/graph';
import { I18nLocale, t } from '@ardoq/i18n';
import { trackUrlLinkClicked } from 'tabview/graphViews/tracking';
import { type ContextMenuAppState } from 'contextMenus/contextMenuRequested';

const openUrl = (url: string): void => {
  openUrlInNewTab(url);
  trackUrlLinkClicked(ViewIds.BLOCKS);
};

const urlOption = (
  { label, url }: UrlFieldValue,
  index: number
): DropdownButtonOption => ({
  type: DropdownOptionType.BUTTON_OPTION,
  name: `open-url-${index}`,
  label,
  iconName: IconName.OPEN_IN_NEW,
  onClick: () => openUrl(url),
});

// One URL field → a single entry; multiple → a submenu (ARD-33060).
const getUrlOptions = (
  urlFieldValues: UrlFieldValue[] | null,
  i18nLocale: I18nLocale
): DropdownOption[] => {
  const urls = (urlFieldValues ?? []).filter(
    ({ url }) => url.trim().length > 0
  );
  if (urls.length === 0) return [];
  if (urls.length === 1) return [urlOption(urls[0], 0)];

  const submenu = {
    type: DropdownOptionType.SUBMENU,
    name: 'open-url',
    label: t(
      i18nLocale,
      'flexibleBlockDiagram:contextMenu.openUrl',
      'Open URL'
    ),
    iconName: IconName.OPEN_IN_NEW,
    options: urls.map(urlOption),
  };

  return [submenu];
};

export const linkContextMenuOptions = (
  view: Controller,
  link: BlocksViewLink,
  { i18nLocale, isViewpointMode }: ContextMenuAppState
): DropdownItem[] => {
  // KFQ12026
  const isCanvasPrototype = hasFeature(Features.CANVAS_PROTOTYPE);

  const editPropertiesMenuItem = getEditPropertiesMenuItem(
    [],
    [link.modelId],
    () => {},
    i18nLocale,
    isViewpointMode
  );

  const deleteReferenceMenuItem = getDeleteReferenceMenuItem(
    [link.modelId],
    () => {},
    i18nLocale,
    isCanvasPrototype
  );

  const historyMenuItem = getHistoryMenuItem(
    link.modelId,
    APIEntityType.REFERENCE,
    () => {},
    i18nLocale
  );

  if (isCanvasPrototype) {
    const options = [
      editPropertiesMenuItem,
      historyMenuItem,
      deleteReferenceMenuItem,
    ];
    return options.filter(isTruthy);
  }

  const options: (DropdownOption | null)[] = [];

  options.push({
    iconName: IconName.ZOOM_TO_FIT,
    label: 'Zoom to fit',
    type: DropdownOptionType.OPTION,
    onClick: () => {
      const fit = rectEncloseRects([
        link.bounds!,
        link.sourceProxy!.bounds,
        link.targetProxy!.bounds,
      ]);
      view?.fit(fit, true);
    },
  });

  options.push({ type: DropdownOptionType.DIVIDER, label: null });

  options.push(editPropertiesMenuItem);
  options.push(deleteReferenceMenuItem);
  options.push(historyMenuItem);

  const urlOptions = getUrlOptions(link.urlFieldValues, i18nLocale);

  if (urlOptions.length > 0) {
    options.push(...urlOptions);
  }

  return options.filter(isTruthy);
};

export const nodeContextMenuOptions = (
  view: Controller,
  node: BlocksViewNode,
  selection: Set<BlocksViewNode>,
  {
    i18nLocale,
    permissionContext,
    featureContext,
    currentContext,
    hasCollapsingRuleApplied,
    workspaceAndModel,
    componentsById,
    referenceCreation,
  }: ContextMenuAppState
): DropdownItem[] => {
  const nodes = selection.has(node) ? Array.from(selection) : [node];
  const componentIds = nodes
    .filter(({ isComponent }) => isComponent)
    .map(({ modelId }) => modelId!);

  if (!componentIds.length) {
    return [];
  }
  const { projection, canvas } = view;
  const [viewportNodeX, viewportNodeY] = projection.toViewport(
    rectCenter(node.bounds)
  );
  const { x: canvasLeft, y: canvasTop } = canvas.getBoundingClientRect();
  const x = canvasLeft + viewportNodeX;
  const y = canvasTop + viewportNodeY;
  const components = componentIds
    .map(componentInterface.get)
    .filter(isComponentEntity);
  const options =
    getComponentMenu({
      components,
      x,
      y,
      isViewpointMode: true,
      eventTargetModelId: node.modelId!,
      target: canvas,
      sourceNode: { viewInstanceId: view.viewInstanceId, nodeId: node.id },
      event: null,
      permissionContext,
      getScenarioContextMenuOptions: getScenarioContextMenuOptionsForView({
        isViewpointMode: true,
        viewId: ViewIds.BLOCKS,
        featureContext,
        permissionContext,
        context: currentContext,
        hasCollapsingRuleApplied,
        i18nLocale,
      }),
      i18nLocale,
      workspaceAndModel,
      componentsById,
      referenceCreation,
    }) ?? [];

  const urlOptions = getUrlOptions(node.urlFieldValues, i18nLocale);

  if (urlOptions.length > 0) {
    options.push(...urlOptions);
  }

  return options;
};

export const rootContextMenuOptions = (_view: Controller): DropdownItem[] => {
  const options: DropdownOption[] = [];

  // perhaps
  //  fit all
  //  expand all
  //  collapse all

  return options;
};
